import React from 'react';
import { Card, Row, Col, Typography, Statistic, Tag, Tooltip } from 'antd';
import { RiseOutlined, FallOutlined, MinusOutlined, TrophyOutlined, BarChartOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

interface DashboardProps {
  returnsAnalysis: any;
}

const Dashboard: React.FC<DashboardProps> = ({ returnsAnalysis }) => {
  const { t } = useTranslation();

  // 获取最佳表现数据
  const getBestPerformance = (): any => {
    if (!returnsAnalysis?.returns_analysis) return null;
    
    // 优先选择 Top 10 的 20 K-lines 数据
    const targetPeriod = 'forward_20';
    const targetRank = 'top_10';
    
    const periodData = returnsAnalysis.returns_analysis[targetPeriod];
    if (periodData && periodData[targetRank]) {
      return {
        period: targetPeriod.replace('forward_', ''),
        rankGroup: targetRank.replace('top_', 'Top '),
        ...periodData[targetRank]
      };
    }
    
    // 如果没有找到目标数据，则回退到原来的逻辑
    let bestData: any = null;
    let bestScore = -Infinity;
    
    Object.entries(returnsAnalysis.returns_analysis).forEach(([period, periodData]: [string, any]) => {
      // 检查所有排名组合
      const rankGroups = ['top_10', 'top_20', 'top_50', 'top_3', 'top_100'];
      
      rankGroups.forEach(rankGroup => {
        const data = periodData[rankGroup];
        if (data && data.mean_return > bestScore) {
          bestScore = data.mean_return;
          bestData = {
            period: period.replace('forward_', ''),
            rankGroup: rankGroup.replace('top_', 'Top '),
            ...data
          };
        }
      });
    });
    
    return bestData;
  };

  // 根据波动率确定颜色和等级
  const getVolatilityStyle = (volatility: number) => {
    if (volatility <= 2.0) {
      return {
        color: '#52c41a',
        level: t('analysis.volatility.low'),
        description: t('analysis.volatility.lowDesc')
      };
    } else if (volatility <= 5.0) {
      return {
        color: '#faad14',
        level: t('analysis.volatility.medium'),
        description: t('analysis.volatility.mediumDesc')
      };
    } else if (volatility <= 10.0) {
      return {
        color: '#ff7a45',
        level: t('analysis.volatility.high'),
        description: t('analysis.volatility.highDesc')
      };
    } else {
      return {
        color: '#ff4d4f',
        level: t('analysis.volatility.extreme'),
        description: t('analysis.volatility.extremeDesc')
      };
    }
  };

  // 生成趋势建议
  const generateSuggestion = (avgReturn: number, volatility: number, winRate: number) => {
    // 基于平均收益、波动率和胜率综合判断
    if (avgReturn > 1.0 && winRate > 60) {
      return {
        type: 'bullish',
        label: t('analysis.suggestion.bullish'),
        color: '#52c41a',
        icon: <RiseOutlined />
      };
    } else if (avgReturn > 0.5 && winRate > 50) {
      return {
        type: 'slightly_bullish',
        label: t('analysis.suggestion.slightlyBullish'),
        color: '#73d13d',
        icon: <RiseOutlined />
      };
    } else if (avgReturn < -1.0 && winRate < 40) {
      return {
        type: 'bearish',
        label: t('analysis.suggestion.bearish'),
        color: '#ff4d4f',
        icon: <FallOutlined />
      };
    } else if (avgReturn < -0.5 && winRate < 50) {
      return {
        type: 'slightly_bearish',
        label: t('analysis.suggestion.slightlyBearish'),
        color: '#ff7a45',
        icon: <FallOutlined />
      };
    } else {
      return {
        type: 'sideways',
        label: t('analysis.suggestion.sideways'),
        color: '#faad14',
        icon: <MinusOutlined />
      };
    }
  };

  const bestPerformance = getBestPerformance();

  if (!bestPerformance) {
    return null;
  }

  const volatilityStyle = getVolatilityStyle(bestPerformance.volatility);
  const suggestion = generateSuggestion(
    bestPerformance.mean_return,
    bestPerformance.volatility,
    bestPerformance.win_rate
  );

  const getReturnColor = (return_: number) => {
    if (return_ > 0) return '#52c41a';
    return '#ff4d4f';
  };

  const getWinRateColor = (rate: number) => {
    if (rate >= 60) return '#52c41a';
    if (rate >= 40) return '#faad14';
    return '#ff4d4f';
  };

  return (
    <div style={{ marginBottom: 24 }}>
      <Title level={4} style={{ textAlign: 'center', marginBottom: 20 }}>
        🎯 {t('analysis.performanceDashboard')}
      </Title>
      <div style={{ textAlign: 'center', marginBottom: 16, color: '#666', fontSize: '12px' }}>
        {t('analysis.dashboardNote')}
      </div>
      
      <Row gutter={[16, 16]}>
        {/* 平均收益 */}
        <Col span={8}>
          <Card 
            size="small" 
            style={{ 
              borderRadius: 8,
              border: `2px solid ${getReturnColor(bestPerformance.mean_return)}`
            }}
          >
            <Statistic
              title={t('analysis.averageReturn')}
              value={bestPerformance.mean_return?.toFixed(2)}
              suffix="%"
              valueStyle={{ 
                color: getReturnColor(bestPerformance.mean_return),
                fontSize: '18px',
                fontWeight: 'bold'
              }}
              prefix={bestPerformance.mean_return >= 0 ? <RiseOutlined /> : <FallOutlined />}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {bestPerformance.rankGroup} ({bestPerformance.period} K-lines)
            </Text>
          </Card>
        </Col>

        {/* 波动率 */}
        <Col span={8}>
          <Card 
            size="small" 
            style={{ 
              borderRadius: 8,
              border: `2px solid ${volatilityStyle.color}`
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Statistic
                title={t('analysis.volatility')}
                value={bestPerformance.volatility?.toFixed(2)}
                valueStyle={{ 
                  color: volatilityStyle.color,
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                prefix={<BarChartOutlined />}
              />
              <Tooltip title={volatilityStyle.description}>
                <InfoCircleOutlined style={{ color: volatilityStyle.color, fontSize: '16px' }} />
              </Tooltip>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
              <Tag color={volatilityStyle.color}>
                {volatilityStyle.level}
              </Tag>
              {/* <Text type="secondary" style={{ fontSize: '11px' }}>
                {t('analysis.volatility.standard')}
              </Text> */}
            </div>
          </Card>
        </Col>

        {/* 胜率 */}
        <Col span={8}>
          <Card 
            size="small" 
            style={{ 
              borderRadius: 8,
              border: `2px solid ${getWinRateColor(bestPerformance.win_rate)}`
            }}
          >
            <Statistic
              title={t('analysis.winRate')}
              value={bestPerformance.win_rate?.toFixed(1)}
              suffix="%"
              valueStyle={{ 
                color: getWinRateColor(bestPerformance.win_rate),
                fontSize: '18px',
                fontWeight: 'bold'
              }}
              prefix={<TrophyOutlined />}
            />
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {bestPerformance.rankGroup} ({bestPerformance.period} K-lines)
            </Text>
          </Card>
        </Col>
      </Row>

      {/* 趋势建议 */}
      <Row style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card 
            size="small" 
            style={{ 
              borderRadius: 8,
              border: `2px solid ${suggestion.color}`,
              background: `${suggestion.color}10`
            }}
          >
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: 8
            }}>
              <span style={{ 
                color: suggestion.color,
                fontSize: '16px'
              }}>
                {suggestion.icon}
              </span>
              <Text strong style={{ 
                fontSize: '16px',
                color: suggestion.color
              }}>
                {t('analysis.suggestion.title')}: {suggestion.label}
              </Text>
              <Tag color={suggestion.color} style={{ marginLeft: 8 }}>
                {suggestion.type}
              </Tag>
            </div>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard; 